import { useState } from "react";
import { TIER_ORDER, DAYS, TIME_START_OPTIONS, COURTS, courtTypeOf } from "../utils/tiers";
import { addHours, durationOptionsFor, getGroupSchedule } from "../utils/time";
import { getScheduleConflicts } from "../utils/validation";

export default function NewGroupModal({ onClose, onCreate, prefill = {}, groups = [] }) {
  const [tier, setTier] = useState(prefill.tier || "Adult Beginner");
  const [day, setDay] = useState(prefill.day || "");
  const [court, setCourt] = useState(prefill.court || "");
  const [startTime, setStartTime] = useState(prefill.startTime || "");
  const [duration, setDuration] = useState(1);

  const courtType = courtTypeOf(court);
  const durationOptions = durationOptionsFor(startTime, courtType);

  // A brand-new group has no id yet; a sentinel keeps getScheduleConflicts
  // from ever matching it against itself.
  const conflicts = getScheduleConflicts(
    { id: "__new__", day, court, startTime, duration },
    groups
  );

  function handleCourtChange(newCourt) {
    setCourt(newCourt);
    const options = durationOptionsFor(startTime, courtTypeOf(newCourt));
    setDuration(d => (options.includes(d) ? d : options[0]));
  }

  function handleStartTimeChange(newStart) {
    setStartTime(newStart);
    const options = durationOptionsFor(newStart, courtType);
    setDuration(d => (options.includes(d) ? d : options[0]));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onCreate({
      tier,
      day,
      startTime,
      duration,
      court,
      courtType,
    });
    onClose();
  }

  const isScheduled = day && startTime && court;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-bold text-gray-800 text-base">New Group</h2>
          <button onClick={onClose} className="text-xl text-gray-400 hover:text-gray-600 leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <Field label="Tier">
            <select
              value={tier}
              onChange={e => setTier(e.target.value)}
              className={selectCls}
            >
              {TIER_ORDER.filter(t => t !== "No Group").map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>

          <Field label="Day">
            <select value={day} onChange={e => setDay(e.target.value)} className={selectCls}>
              <option value="">— Unscheduled —</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>

          <Field label="Court">
            <select value={court} onChange={e => handleCourtChange(e.target.value)} className={selectCls}>
              <option value="">— None —</option>
              {COURTS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Time">
              <select value={startTime} onChange={e => handleStartTimeChange(e.target.value)} className={selectCls}>
                <option value="">— Unscheduled —</option>
                {TIME_START_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>

            <Field label="Duration">
              <select
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                disabled={courtType === "Pickleball"}
                className={`${selectCls} ${courtType === "Pickleball" ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {durationOptions.map(h => (
                  <option key={h} value={h}>{h} hour{h !== 1 ? "s" : ""}</option>
                ))}
              </select>
            </Field>
          </div>

          {courtType === "Pickleball" && (
            <p className="text-xs text-gray-500 -mt-2">Pickleball sessions are fixed at 1 hour.</p>
          )}

          {startTime && (
            <p className="text-xs text-gray-500 -mt-2">Ends at {addHours(startTime, duration)}.</p>
          )}

          {!isScheduled && (day || startTime || court) && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Set day, start time, and court to fully schedule this group.
            </p>
          )}

          {conflicts.length > 0 && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2">
              <span className="font-semibold">⚠ Overlaps another group on this court:</span>
              <ul className="mt-0.5 list-disc list-inside">
                {conflicts.map(g => {
                  const s = getGroupSchedule(g);
                  return <li key={g.id}>{g.id} · {g.day} · {s.startTime}–{addHours(s.startTime, s.duration)}</li>;
                })}
              </ul>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 rounded px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white rounded px-4 py-2 text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Create Group
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const selectCls =
  "border border-gray-300 rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white";
